import { useMemo, useState } from 'react';
import { ChevronDown, Download, Search, ShieldCheck, TrendingUp } from 'lucide-react';
import { Button } from '../../components/Button';
import { Card } from '../../components/Card';
import { Badge } from '../../components/Badge';
import { Input } from '../../components/Input';
import { Money } from '../../components/Money';
import { PageHeader } from '../../components/PageHeader';
import { cn } from '../../lib/cn';
import { useCurrentReseller, useResellerPayments } from '../../api/reseller';
import { formatDate } from '../../lib/format';

const STATUS_TONE = {
  paid: 'success' as const,
  failed: 'error' as const,
  refunded: 'neutral' as const,
};

const STATUS_LABEL = {
  paid: 'Paid',
  failed: 'Failed',
  refunded: 'Refunded',
};

/**
 * Reseller-side payments view — `/reseller/payments`.
 *
 * Lists every customer payment under THIS reseller, with the gross amount,
 * the platform's commission cut, and the reseller's net commission earned.
 *
 * Scoping: rigidly filtered to the active reseller. The reseller never sees
 * payments under other resellers — enforced at the API by RLS in Postgres.
 */
export function Payments() {
  const { data: currentReseller } = useCurrentReseller();
  const { data: resellerPayments } = useResellerPayments();

  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'paid' | 'failed' | 'refunded'>('all');
  const [methodFilter, setMethodFilter] = useState<'all' | 'SSLCommerz' | 'Cash'>('all');

  // /reseller/payments is already RLS-scoped to this reseller — no client filter needed.
  const myPayments = resellerPayments ?? [];

  const filtered = useMemo(() => {
    return myPayments.filter((p) => {
      if (statusFilter !== 'all' && p.status !== statusFilter) return false;
      if (methodFilter !== 'all' && p.method !== methodFilter) return false;
      if (search) {
        const s = search.toLowerCase();
        if (!p.invoiceNumber.toLowerCase().includes(s) && !p.businessName.toLowerCase().includes(s)) return false;
      }
      return true;
    });
  }, [myPayments, statusFilter, methodFilter, search]);

  const stats = useMemo(() => {
    const paid = myPayments.filter((p) => p.status === 'paid');
    const totalGross = paid.reduce((s, p) => s + p.amountMinor, 0);
    const totalCommission = paid.reduce((s, p) => s + p.commissionMinor, 0);
    const failed = myPayments.filter((p) => p.status === 'failed').length;
    return { totalGross, totalCommission, failed, paidCount: paid.length };
  }, [myPayments]);

  if (!currentReseller) {
    return (
      <div className="px-4 sm:px-6 lg:px-8 py-10 text-center text-sm text-zinc-500 dark:text-zinc-400">
        Loading payments…
      </div>
    );
  }
  const r = currentReseller;
  if (!r) return null;

  function exportCsv() {
    const head = ['received_at', 'invoice', 'business', 'gross_bdt', 'commission_bdt', 'method', 'status'].join(',');
    const rows = filtered.map((p) =>
      [p.receivedAt, p.invoiceNumber, p.businessName, p.amountMinor, p.commissionMinor, p.method, p.status]
        .map((v) => `"${String(v).replace(/"/g, '""')}"`)
        .join(','),
    );
    const csv = [head, ...rows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${r?.slug ?? 'reseller'}-payments-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-6 sm:py-8 max-w-[1440px] mx-auto">
      <PageHeader
        eyebrow="Money"
        title="Payments"
        subtitle={`Every customer payment under ${r.name}. Gross paid by customer · platform's ${r.commissionPct}% commission cut · what you receive.`}
        action={
          <Button variant="secondary" size="sm" iconLeft={<Download className="w-4 h-4" />} onClick={exportCsv}>
            <span className="hidden sm:inline">Export CSV</span>
          </Button>
        }
      />

      {/* KPI strip */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-6">
        <StatTile
          label="Gross collected"
          value={<Money minor={stats.totalGross} hideCents tone="neutral" className="text-zinc-950 dark:text-zinc-50" />}
          subtitle={`${stats.paidCount} paid invoice${stats.paidCount === 1 ? '' : 's'}`}
        />
        <StatTile
          label={`Platform commission · ${r.commissionPct}%`}
          value={<Money minor={stats.totalGross - stats.totalCommission} hideCents tone="neutral" className="text-zinc-700 dark:text-zinc-200" />}
          subtitle="kept by ITCity"
        />
        <StatTile
          label="Your commission"
          value={<Money minor={stats.totalCommission} hideCents tone="neutral" className="text-emerald-700 dark:text-emerald-300" />}
          subtitle={`${100 - r.commissionPct}% of gross — paid out on schedule`}
        />
        <StatTile
          label="Failed payments"
          value={<span className="tabular-nums text-zinc-950 dark:text-zinc-50">{stats.failed}</span>}
          subtitle="auto-retried + dunning"
        />
      </div>

      <Card className="overflow-hidden">
        {/* Filter chip row */}
        <div className="px-5 sm:px-6 py-3 flex flex-wrap items-center gap-2 border-b border-zinc-200 dark:border-zinc-700">
          <FilterDropdown
            label="Status"
            value={statusFilter === 'all' ? 'All' : STATUS_LABEL[statusFilter]}
            options={[
              { v: 'all', l: 'All' },
              { v: 'paid', l: 'Paid' },
              { v: 'failed', l: 'Failed' },
              { v: 'refunded', l: 'Refunded' },
            ]}
            onChange={(v) => setStatusFilter(v as typeof statusFilter)}
          />
          <FilterDropdown
            label="Method"
            value={methodFilter === 'all' ? 'All' : methodFilter}
            options={[
              { v: 'all', l: 'All methods' },
              { v: 'SSLCommerz', l: 'SSLCommerz (online)' },
              { v: 'Cash', l: 'Cash (manual)' },
            ]}
            onChange={(v) => setMethodFilter(v as typeof methodFilter)}
          />
          <div className="ml-auto w-full sm:w-72">
            <Input
              placeholder="Search invoice or business…"
              prefix={<Search className="w-4 h-4" />}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
        </div>

        {/* Table */}
        <div className="overflow-x-auto nice-scroll">
          <table className="w-full text-sm">
            <thead className="text-xs uppercase tracking-wide text-zinc-500 dark:text-zinc-400 bg-zinc-50/50 dark:bg-zinc-900/50">
              <tr>
                <th className="text-left font-medium px-5 sm:px-6 py-3">Date</th>
                <th className="text-left font-medium px-4 py-3">Business</th>
                <th className="text-left font-medium px-4 py-3 hidden md:table-cell">Invoice</th>
                <th className="text-right font-medium px-4 py-3">Gross paid</th>
                <th className="text-right font-medium px-4 py-3 hidden lg:table-cell">Platform cut</th>
                <th className="text-right font-medium px-4 py-3">You earned</th>
                <th className="text-left font-medium px-4 py-3 hidden sm:table-cell">Method</th>
                <th className="text-left font-medium px-5 sm:px-6 py-3">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-200 dark:divide-zinc-700">
              {filtered.length === 0 ? (
                <tr>
                  <td colSpan={8} className="px-6 py-16 text-center">
                    <TrendingUp className="w-8 h-8 text-zinc-400 dark:text-zinc-500 mx-auto mb-3" />
                    <h3 className="text-base font-semibold text-zinc-950 dark:text-zinc-50">
                      No payments {search || statusFilter !== 'all' || methodFilter !== 'all' ? 'match your filters' : 'yet'}
                    </h3>
                    <p className="mt-1 text-sm text-zinc-500 dark:text-zinc-400">
                      {search || statusFilter !== 'all' || methodFilter !== 'all'
                        ? 'Try a broader filter.'
                        : 'Once your customers pay their first invoice, payments + commissions appear here.'}
                    </p>
                  </td>
                </tr>
              ) : (
                filtered.map((p) => {
                  const platformCut = p.amountMinor - p.commissionMinor;
                  return (
                    <tr key={p.id} className="hover:bg-zinc-50 dark:hover:bg-zinc-800/70 transition">
                      <td className="px-5 sm:px-6 py-4 text-zinc-700 dark:text-zinc-200 tabular-nums whitespace-nowrap">
                        {formatDate(p.receivedAt)}
                      </td>
                      <td className="px-4 py-4 text-zinc-900 dark:text-zinc-100 font-medium">
                        {p.businessName}
                      </td>
                      <td className="px-4 py-4 mono text-xs text-zinc-600 dark:text-zinc-300 hidden md:table-cell whitespace-nowrap">
                        {p.invoiceNumber}
                      </td>
                      <td className="px-4 py-4 text-right whitespace-nowrap">
                        <Money minor={p.amountMinor} hideCents tone="neutral" className="text-zinc-950 dark:text-zinc-50 font-medium" />
                      </td>
                      <td className="px-4 py-4 text-right whitespace-nowrap hidden lg:table-cell">
                        <Money minor={platformCut} hideCents tone="neutral" className="text-zinc-500 dark:text-zinc-400" />
                      </td>
                      <td className="px-4 py-4 text-right whitespace-nowrap">
                        <Money minor={p.commissionMinor} hideCents tone="neutral" className="text-emerald-700 dark:text-emerald-300 font-medium" />
                      </td>
                      <td className="px-4 py-4 text-zinc-600 dark:text-zinc-300 hidden sm:table-cell">{p.method}</td>
                      <td className="px-5 sm:px-6 py-4">
                        <Badge tone={STATUS_TONE[p.status]}>{STATUS_LABEL[p.status]}</Badge>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        <div className="px-5 sm:px-6 py-3 border-t border-zinc-200 dark:border-zinc-700 text-xs text-zinc-500 dark:text-zinc-400 flex items-center justify-between flex-wrap gap-2">
          <span>
            Showing <span className="tabular-nums font-medium text-zinc-700 dark:text-zinc-200">{filtered.length}</span> of{' '}
            <span className="tabular-nums">{myPayments.length}</span> payments under <span className="font-medium text-zinc-700 dark:text-zinc-200">{r.name}</span>
          </span>
          <span className="inline-flex items-center gap-1.5">
            <ShieldCheck className="w-3 h-3" />
            Scoped to your reseller account · other resellers' payments hidden
          </span>
        </div>
      </Card>
    </div>
  );
}

function StatTile({
  label,
  value,
  subtitle,
}: {
  label: string;
  value: React.ReactNode;
  subtitle: string;
}) {
  return (
    <Card className="p-4 sm:p-5">
      <div className="text-xs font-medium uppercase tracking-wider text-zinc-500 dark:text-zinc-400">{label}</div>
      <div className="mt-1.5 text-xl sm:text-2xl font-semibold">{value}</div>
      <div className="mt-1 text-xs text-zinc-500 dark:text-zinc-400">{subtitle}</div>
    </Card>
  );
}

function FilterDropdown({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: string;
  options: Array<{ v: string; l: string }>;
  onChange: (v: string) => void;
}) {
  const [open, setOpen] = useState(false);
  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium bg-white dark:bg-zinc-900 text-zinc-700 dark:text-zinc-200 border border-zinc-200 dark:border-zinc-700 hover:bg-zinc-50 dark:hover:bg-zinc-800 transition"
      >
        <span className="text-zinc-500 dark:text-zinc-400">{label}:</span>
        <span>{value}</span>
        <ChevronDown className="w-3 h-3 text-zinc-400" />
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setOpen(false)} />
          <div className="absolute top-full mt-1 left-0 z-20 min-w-[180px] rounded-md border border-zinc-200 dark:border-zinc-600 bg-white dark:bg-zinc-900 shadow-md py-1">
            {options.map((o) => (
              <button
                key={o.v}
                type="button"
                onClick={() => {
                  onChange(o.v);
                  setOpen(false);
                }}
                className={cn(
                  'block w-full text-left px-3 py-1.5 text-xs hover:bg-zinc-50 dark:hover:bg-zinc-800',
                  'text-zinc-700 dark:text-zinc-200',
                )}
              >
                {o.l}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
